import { useState, useMemo, useCallback, useEffect, useContext } from "react";
import {
    Box,
    Stack,
    Typography,
    Card,
    Grid,
    CardContent,
} from "@mui/material";
import { useGridApiRef, getGridStringOperators } from "@mui/x-data-grid";
import Chart from "../../components/Chart"
import NodataImg from "../../assets/images/noData.png";
import { useDispatch, useSelector } from "react-redux";
import { UserRetentionApi } from "../../store/analytics/userRetention";
import { ThemeContext } from '../../context/ThemeContext';
import dayjs from "dayjs";
import CustomGridToolbar from "../../components/CustomGridToolbar";
import apiRoutes from "../../store/apiConfigs";
import BackdropUI from "../../ui/Backdrop";
import DataGridComponent from "../../components/DataGridComponent";
import { AiOutlineBell } from 'react-icons/ai'
import { useNavigate } from "react-router-dom";
import { UsageSummaryApi } from "../../store/analytics/usageSummaryTable";

const UsageSummaryTableColumn = [
    {
        field: "user_id",
        headerName: "User ID",
        width: 100,
        filterable: false,
        sortable: true,
        disableColumnMenu: true,
    },
    {
        field: "fullname",
        headerName: "Full Name",
        width: 200,
        filterable: false,
        sortable: true,
        disableColumnMenu: true,
    },
    {
        field: "username",
        headerName: "User Name",
        width: 250,
        filterable: false,
        sortable: true,
        disableColumnMenu: true,
    },
    {
        field: "phone_number",
        headerName: "Phone Number",
        width: 200,
        filterable: false,
        sortable: true,
        disableColumnMenu: true,
    },
    {
        field: "content_type",
        headerName: "Content Type",
        width: 200,
        filterable: false,
        sortable: true,
        disableColumnMenu: true,
        renderCell: (data) => (
            <Typography sx={{ overflowWrap: "anywhere" }}>
                {data?.row?.content_type?.replace(/_/g, " ")}
            </Typography>
        ),

    },
    {
        field: "content_title",
        headerName: "Content Title",
        width: 300,
        filterable: false,
        sortable: true,
        disableColumnMenu: true,
    },
    {
        field: "enrollment_date",
        headerName: "Enrollment Date",
        width: 200,
        filterable: false,
        sortable: true,
        disableColumnMenu: true,
        renderCell: (data) => (
            <Typography sx={{ overflowWrap: "anywhere" }}>
                {data?.row?.enrollment_date && dayjs(data.row.enrollment_date).format("YYYY-MM-DD")}
            </Typography>
        ),
    },
    {
        field: "duration",
        headerName: "Duration",
        width: 120,
        filterable: false,
        sortable: true,
        disableColumnMenu: true,
    },
    {
        field: "passed",
        headerName: "Passed",
        width: 100,
        filterable: false,
        sortable: true,
        disableColumnMenu: true,
        renderCell: (data) => (
            <Typography sx={{ overflowWrap: "anywhere" }}>
                {data?.row?.passed === true ? "Yes" : "No"}
            </Typography>
        ),
    },
    {
        field: "certificate_generation",
        headerName: "Certificate Generated",
        width: 200,
        filterable: false,
        sortable: true,
        disableColumnMenu: true,
        renderCell: (data) => (
            <Typography>{data?.row?.certificate_generation ? "Generated" : "Not Generated"}</Typography>
        ),
    },
    {
        field: "platform",
        headerName: "Platform",
        width: 100,
        filterable: false,
        sortable: true,
        disableColumnMenu: true,
    },
    {
        field: "feedback_rating",
        headerName: "Feedback Rating",
        width: 200,
        filterable: false,
        sortable: true,
        disableColumnMenu: true,
    },
    {
        field: "grade",
        headerName: "Class",
        width: 100,
        filterable: false,
        sortable: true,
        disableColumnMenu: true,
    },

];

export default function UsageSummary({ graphData, region, state, loading, type, module, monthDaily, startDate, endDate, isMaskingEnabled, schoolType, platform, userType, language }) {
    const [activevsavgtimespent, setActiveVsAvgTimeSpent] = useState({ l1: true, l2: true });
    const [teacherRegister, setTeacherRegistration] = useState({ l1: true, l2: true, l3: true, l4: true });
    const apiRef = useGridApiRef();
    const dispatch = useDispatch();
    const navigate = useNavigate()
    const [isDownload, setIsDownload] = useState(false)
    const UsageSummaryTable = useSelector((state) => state?.UsageSummaryTable);
    const { result: tableResult, loading: tableLoading, total } = UsageSummaryTable;
    const [queryOptions, setQueryOptions] = useState({});
    const { paginationModel, setPaginationModel } = useContext(ThemeContext)

    const onFilterChange = useCallback((filterModel) => {
        setQueryOptions({ filterModel: { ...filterModel } });
    }, []);
    const toggleTeacherRegistration = useCallback((key) => {
        setTeacherRegistration(prevState => ({ ...prevState, [key]: !prevState[key] }));
    }, []);
    const toggleActiveVsAvgTimeSpent = useCallback((key) => {
        setActiveVsAvgTimeSpent(prevState => ({ ...prevState, [key]: !prevState[key] }));
    }, []);
    const getRowSpacing = useCallback((params) => {
        return {
            top: params.isFirstVisible ? 0 : 5,
            bottom: params.isLastVisible ? 0 : 5,
        };
    }, []);
    const handleSortModelChange = useCallback((sortModel) => {
        setQueryOptions({ sortModel: [...sortModel] });
    }, []);

    const transformGraphData = useMemo(() => {
        const chartData = Array.isArray(graphData?.data)
            ? graphData?.data.map((item) => {
                const dataPoint = { name: item.unique_users };
                dataPoint["Total Hours"] = item.total_hours || 0;
                dataPoint["Unique Users"] = item.unique_users;
                dataPoint["Platform"] = item.platform;
                dataPoint["Unique Content"] = item.unique_content;
                dataPoint["Sessions"] = item.sessions;
                dataPoint["Percentage"] = item.percentage;
                return dataPoint;
            })
            : [];
        return chartData;
    }, [graphData]);

    useEffect(() => {
        // console.log("isMaskingEnabled",isMaskingEnabled);
        const filterModel = queryOptions?.filterModel;
        const items = filterModel?.items;
        const firstItem = items && items.length > 0 ? items[0] : {};
        const quickFilterValues = filterModel?.quickFilterValues;
        const sortModel = queryOptions?.sortModel;
        let query
        if (sortModel?.length > 0) {
            query = {
                school_type: schoolType,
                page: paginationModel?.page + 1,
                page_size: paginationModel?.pageSize,
                sort_order: sortModel[0].sort || "desc",
                field: firstItem.field || '',
                operator: firstItem.operator || (quickFilterValues && "contains") || '',
                value: firstItem.value || (quickFilterValues && quickFilterValues.toString().replaceAll(",", " ")) || ''
            };
        } else {
            query = {
                school_type: schoolType,
                page: paginationModel?.page + 1,
                page_size: paginationModel?.pageSize,
                field: firstItem.field || '',
                state: state,
                city: region,
                sort_order: "desc",
                start_date: startDate,
                end_date: endDate,
                platform: platform,
                operator: firstItem.operator || (quickFilterValues && "contains") || '',
                value: firstItem.value || (quickFilterValues && quickFilterValues.toString().replaceAll(",", " ")) || ''
            };
        }
        dispatch(UsageSummaryApi(query));
    }, [dispatch, queryOptions, paginationModel, region, state, module, startDate, endDate, isMaskingEnabled, schoolType, platform, monthDaily]);

    console.log("graphData?.total_data-->", graphData)
    return (
        <>
            <Stack direction="row" spacing={1} sx={{ my: 2, padding: "10px" }} alignItems="center" justifyContent={"space-between"}>
                <Typography variant="h3">Usage Summary</Typography>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ my: 2 }} alignItems="center">
                <Grid container spacing={1}>
                    {graphData?.total_data &&
                        Object.entries(graphData.total_data)
                            .filter(([data]) => {

                                return [
                                    "course_enrolled",
                                    "course_completed",
                                    "assessment_taken",
                                    "active_users",
                                    "average_time_spent",
                                ].includes(data);
                            })
                            .map(([data, value], index) => {
                                return (
                                    <Grid item xs={6} sm={6} md={6} lg={3} mb={1} key={data}>
                                        <Box
                                            className="customCard1"
                                            sx={{
                                                transition: "none !important",
                                                "&:hover": {
                                                    transform: "none !important",
                                                },
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Stack>
                                                    <Typography sx={{ textTransform: "capitalize" }}>
                                                        {data.replaceAll("_", " ")}
                                                    </Typography>
                                                    <Typography variant="h4" fontWeight={600}>
                                                        {value}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        </Box>
                                    </Grid>
                                );
                            })}
                </Grid>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ my: 2, padding: "10px", width: '100%' }} alignItems="center">
                <Card sx={{ width: '100%', mb: 2 }}>
                    <CardContent>
                        <Chart
                            type={"line"}
                            height={500}
                            data={transformGraphData}
                            module={"usageSummary"}
                            lineState={teacherRegister}
                        />
                    </CardContent>
                </Card>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ my: 2, padding: "10px" }} alignItems="center">
                <DataGridComponent
                    sortingOrder={[]}
                    disableColumnMenu
                    disableColumnSorting
                    loading={tableLoading}
                    rowCount={total}
                    pageSize={paginationModel?.pageSize}
                    onPageSizeChange={(newPageSize) => {
                        setPaginationModel({
                            ...paginationModel,
                            pageSize: newPageSize,
                        })
                    }}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    sx={{
                        height: 500,
                        "& .MuiDataGrid-cell": {
                            cursor: "pointer",
                            fontSize: "16px",
                        },
                        "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
                            visibility: "hidden",
                        },
                        "& .MuiDataGrid-columnHeader": {
                            "& .MuiDataGrid-sortIcon": {
                                display: "none",
                            },
                            "& .MuiDataGrid-menuIcon": {
                                display: "none",
                            },
                        },
                    }}
                    rows={tableResult || []}
                    getRowId={(row) => `${row.username}-${row.created_at}-${row.phone_number}-${row.duration}`}
                    getRowSpacing={getRowSpacing}
                    columns={UsageSummaryTableColumn}
                    // onSortModelChange={handleSortModelChange}
                    onFilterModelChange={onFilterChange}
                    slots={{
                        noRowsOverlay: () => (
                            <Box className="noData">
                                <img src={NodataImg} alt="no data found" />
                            </Box>
                        ),
                        toolbar: (props) =>
                        (CustomGridToolbar({
                            ...props, moduleType: "CourseProgress",
                            queryOptions: queryOptions,
                            paginationModel: paginationModel,
                            isDownload: isDownload,
                            setIsDownload: setIsDownload,
                            apiUrl: apiRoutes.usageSummaryCsvExport,
                            hasfilter: false,
                            hasSearch: false,
                            additionalQueryParams: {
                                state: state,
                                city: region,
                                start_date: startDate,
                                end_date: endDate,
                                platform: platform,
                            }
                        })),
                    }}
                    slotProps={{
                        loadingOverlay: {
                            variant: 'linear-progress',
                            noRowsVariant: 'linear-progress',
                        },
                        toolbar: {
                            // showQuickFilter: true,
                            showQuickFilter: false,
                        },
                    }}
                />
            </Stack>
            <BackdropUI open={isDownload} />
        </>)
}
